import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/documentNode';
import * as Apollo from '@apollo/client';
export const AllBoardsAndWorkspacesDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"AllBoardsAndWorkspaces"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"memberId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"member"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"memberId"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"boards"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"filter"},"value":{"kind":"ListValue","values":[{"kind":"EnumValue","value":"open"},{"kind":"EnumValue","value":"starred"}]}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"closed"}},{"kind":"Field","name":{"kind":"Name","value":"dateLastView"}},{"kind":"Field","name":{"kind":"Name","value":"idOrganization"}},{"kind":"Field","name":{"kind":"Name","value":"organization"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"displayName"}},{"kind":"Field","name":{"kind":"Name","value":"logoHash"}},{"kind":"Field","name":{"kind":"Name","value":"offering"}}]}},{"kind":"Field","name":{"kind":"Name","value":"url"}}]}},{"kind":"Field","name":{"kind":"Name","value":"organizations"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"displayName"}},{"kind":"Field","name":{"kind":"Name","value":"enterprise"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"displayName"}},{"kind":"Field","name":{"kind":"Name","value":"logoHash"}}]}},{"kind":"Field","name":{"kind":"Name","value":"logoHash"}},{"kind":"Field","name":{"kind":"Name","value":"name"}}]}},{"kind":"Field","name":{"kind":"Name","value":"username"}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"AllBoardsAndWorkspaces","document":AllBoardsAndWorkspacesDocument}} as const;
export type AllBoardsAndWorkspacesQueryVariables = Types.Exact<{
  memberId: Types.Scalars['ID']['input'];
}>;


export type AllBoardsAndWorkspacesQuery = (
  { __typename: 'Query' }
  & { member?: Types.Maybe<(
    { __typename: 'Member' }
    & Pick<Types.Member, 'id' | 'username'>
    & { boards: Array<(
      { __typename: 'Board' }
      & Pick<Types.Board, 'id' | 'closed' | 'dateLastView' | 'idOrganization' | 'url'>
      & { organization?: Types.Maybe<(
        { __typename: 'Organization' }
        & Pick<Types.Organization, 'id' | 'displayName' | 'logoHash' | 'offering'>
      )> }
    )>, organizations: Array<(
      { __typename: 'Organization' }
      & Pick<Types.Organization, 'id' | 'displayName' | 'logoHash' | 'name'>
      & { enterprise?: Types.Maybe<(
        { __typename: 'Enterprise' }
        & Pick<Types.Enterprise, 'id' | 'displayName' | 'logoHash'>
      )> }
    )> }
  )> }
);

/**
 * __useAllBoardsAndWorkspacesQuery__
 *
 * To run a query within a React component, call `useAllBoardsAndWorkspacesQuery` and pass it any options that fit your needs.
 * When your component renders, `useAllBoardsAndWorkspacesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAllBoardsAndWorkspacesQuery({
 *   variables: {
 *      memberId: // value for 'memberId'
 *   },
 * });
 */
export function useAllBoardsAndWorkspacesQuery(
  baseOptions: TrelloQueryHookOptions<
    AllBoardsAndWorkspacesQuery,
    AllBoardsAndWorkspacesQueryVariables
  > &
    (
      | { variables: AllBoardsAndWorkspacesQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: AllBoardsAndWorkspacesDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    AllBoardsAndWorkspacesQuery,
    AllBoardsAndWorkspacesQueryVariables
  >(AllBoardsAndWorkspacesDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function useAllBoardsAndWorkspacesLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    AllBoardsAndWorkspacesQuery,
    AllBoardsAndWorkspacesQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    AllBoardsAndWorkspacesQuery,
    AllBoardsAndWorkspacesQueryVariables
  >(AllBoardsAndWorkspacesDocument, options);
}
export function useAllBoardsAndWorkspacesSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        AllBoardsAndWorkspacesQuery,
        AllBoardsAndWorkspacesQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    AllBoardsAndWorkspacesQuery,
    AllBoardsAndWorkspacesQueryVariables
  >(AllBoardsAndWorkspacesDocument, options);
}
export type AllBoardsAndWorkspacesQueryHookResult = ReturnType<
  typeof useAllBoardsAndWorkspacesQuery
>;
export type AllBoardsAndWorkspacesLazyQueryHookResult = ReturnType<
  typeof useAllBoardsAndWorkspacesLazyQuery
>;
export type AllBoardsAndWorkspacesSuspenseQueryHookResult = ReturnType<
  typeof useAllBoardsAndWorkspacesSuspenseQuery
>;
export type AllBoardsAndWorkspacesQueryResult = Apollo.QueryResult<
  AllBoardsAndWorkspacesQuery,
  AllBoardsAndWorkspacesQueryVariables
>;
